// components/RecordingStudio/MusicLibrary.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Play,
  Pause,
  Search,
  Music,
  Check,
  Loader,
  Disc3,
} from "lucide-react";
import api from "../../services/api";

const WaveformBars = ({ isPlaying, count = 32 }) => {
  const heights = [
    3, 7, 5, 9, 4, 8, 6, 10, 3, 7, 5, 9, 6, 4, 8, 3, 9, 5, 7, 4, 10, 6, 3, 8, 5,
    7, 4, 6, 8, 5, 9, 4,
  ];
  return (
    <div className="flex items-center gap-[2px] h-8">
      {heights.slice(0, count).map((h, i) => (
        <div
          key={i}
          style={{
            height: `${h * 3}px`,
            animationDelay: isPlaying ? `${i * 35}ms` : undefined,
            transition: "height 0.3s ease",
          }}
          className={`w-[2px] rounded-full flex-shrink-0 ${
            isPlaying ? "animate-pulse" : ""
          }`}
          style={{
            height: isPlaying ? `${h * 3}px` : "4px",
            width: "2px",
            borderRadius: "9999px",
            backgroundColor: isPlaying ? "#a78bfa" : "rgba(255,255,255,0.12)",
            animationDelay: `${i * 35}ms`,
            transition: "height 0.2s ease, background-color 0.2s ease",
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
};

const formatDuration = (seconds) => {
  if (!seconds) return "--:--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
    style={{
      background: active ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.04)",
      border: active
        ? "1px solid rgba(139,92,246,0.5)"
        : "1px solid rgba(255,255,255,0.07)",
      color: active ? "#c4b5fd" : "#6b7280",
    }}
  >
    {label}
  </button>
);

const FilterSection = ({ title, items, selected, onSelect }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-2 px-1">
        {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <FilterChip
          label="All"
          active={!selected}
          onClick={() => onSelect(null)}
        />
        {items.map((item) => (
          <FilterChip
            key={item}
            label={item}
            active={selected === item}
            onClick={() => onSelect(selected === item ? null : item)}
          />
        ))}
      </div>
    </div>
  );
};

const MusicLibrary = ({ onSelect, onClose }) => {
  const [tracks, setTracks] = useState([]);
  const [filters, setFilters] = useState({ genres: [], moods: [], tempos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedTempo, setSelectedTempo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const audioRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(
      () => setDebouncedSearch(searchQuery),
      400,
    );
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    loadTracks();
  }, [selectedGenre, selectedMood, selectedTempo, debouncedSearch]);

  const loadTracks = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedGenre) params.genre = selectedGenre;
      if (selectedMood) params.mood = selectedMood;
      if (selectedTempo) params.tempo = selectedTempo;
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await api.getMusicLibrary(params);
      setTracks(response.tracks || []);
      setFilters(response.filters || { genres: [], moods: [], tempos: [] });
    } catch (err) {
      console.error("Failed to load music library:", err);
      setError("Failed to load tracks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = useCallback(
    (e, track) => {
      e.stopPropagation();
      if (playingTrackId === track.id) {
        audioRef.current?.pause();
        setPlayingTrackId(null);
        return;
      }
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(track.audioUrl);
      audioRef.current.volume = 0.7;
      audioRef.current.play().catch(() => {});
      audioRef.current.addEventListener("ended", () => setPlayingTrackId(null));
      setPlayingTrackId(track.id);
    },
    [playingTrackId],
  );

  useEffect(() => () => audioRef.current?.pause(), []);

  const handleSelectTrack = (track) => {
    audioRef.current?.pause();
    setPlayingTrackId(null);
    onSelect(track);
  };

  const resetFilters = () => {
    setSelectedGenre(null);
    setSelectedMood(null);
    setSelectedTempo(null);
    setSearchQuery("");
  };

  const hasActiveFilters =
    selectedGenre || selectedMood || selectedTempo || searchQuery;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(12px)" }}
    >
      <div
        className="w-full max-w-6xl max-h-[90vh] flex flex-col rounded-3xl shadow-2xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #0f1117 0%, #0a0c12 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-8 py-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #d97706, #b45309)",
              }}
            >
              <Disc3 size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">
                Music Library
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                {loading
                  ? "Loading…"
                  : `${tracks.length} track${tracks.length !== 1 ? "s" : ""}${hasActiveFilters ? " (filtered)" : ""}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-600 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Search bar */}
        <div
          className="px-8 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
        >
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600"
              size={14}
            />
            <input
              type="text"
              placeholder="Search tracks, artists, moods…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-700 focus:outline-none transition-all"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = "rgba(217,119,6,0.5)")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = "rgba(255,255,255,0.07)")
              }
            />
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar filters */}
          <div
            className="w-52 flex-shrink-0 overflow-y-auto p-4"
            style={{
              borderRight: "1px solid rgba(255,255,255,0.05)",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.1) transparent",
            }}
          >
            <FilterSection
              title="Genre"
              items={filters.genres}
              selected={selectedGenre}
              onSelect={setSelectedGenre}
            />
            <FilterSection
              title="Mood"
              items={filters.moods}
              selected={selectedMood}
              onSelect={setSelectedMood}
            />
            <FilterSection
              title="Tempo"
              items={filters.tempos}
              selected={selectedTempo}
              onSelect={setSelectedTempo}
            />

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="w-full mt-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:text-violet-400 transition-colors"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                Clear all filters
              </button>
            )}
          </div>

          {/* Track list */}
          <div
            className="flex-1 overflow-y-auto"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.1) transparent",
            }}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <div
                  className="w-8 h-8 rounded-full animate-spin"
                  style={{
                    border: "2px solid rgba(217,119,6,0.2)",
                    borderTopColor: "#d97706",
                  }}
                />
                <p className="text-sm text-gray-600">Loading tracks…</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <p className="text-sm text-red-400">{error}</p>
                <button
                  onClick={loadTracks}
                  className="text-xs text-amber-500 hover:text-amber-300"
                >
                  Try again
                </button>
              </div>
            ) : tracks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 gap-2">
                <Music size={28} className="text-gray-700" />
                <p className="text-sm text-gray-600">No tracks found</p>
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="text-xs text-amber-500 hover:text-amber-300"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr
                    className="text-[10px] font-bold uppercase tracking-widest text-gray-600 sticky top-0"
                    style={{
                      background: "rgba(10,12,18,0.95)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <th className="text-left py-3 pl-6 w-10">#</th>
                    <th className="text-left py-3 pl-3">Track</th>
                    <th className="text-left py-3 hidden lg:table-cell pr-4">
                      Wave
                    </th>
                    <th className="text-left py-3 hidden md:table-cell">
                      Genre
                    </th>
                    <th className="text-left py-3 hidden md:table-cell">
                      Mood
                    </th>
                    <th className="text-right py-3 pr-4">Time</th>
                    <th className="py-3 pr-4 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track, i) => {
                    const isPlaying = playingTrackId === track.id;
                    const isHovered = hoveredRow === track.id;
                    return (
                      <tr
                        key={track.id}
                        className="cursor-pointer transition-all duration-100"
                        style={{
                          background: isHovered
                            ? "rgba(217,119,6,0.06)"
                            : "transparent",
                        }}
                        onMouseEnter={() => setHoveredRow(track.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        onClick={() => handleSelectTrack(track)}
                      >
                        {/* Index / play */}
                        <td className="py-2.5 pl-6 w-10">
                          <div className="w-6 h-6 flex items-center justify-center relative">
                            {isPlaying ? (
                              <button
                                onClick={(e) => handlePlayPause(e, track)}
                                className="text-amber-400"
                              >
                                <Pause size={13} />
                              </button>
                            ) : isHovered ? (
                              <button
                                onClick={(e) => handlePlayPause(e, track)}
                                className="text-gray-300 hover:text-white"
                              >
                                <Play size={13} />
                              </button>
                            ) : (
                              <span className="text-[11px] text-gray-700 font-mono tabular-nums">
                                {i + 1}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Title + artist */}
                        <td className="py-2.5 pl-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center"
                              style={{ background: "rgba(255,255,255,0.05)" }}
                            >
                              {track.thumbnailUrl ? (
                                <img
                                  src={track.thumbnailUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Music size={14} className="text-gray-700" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p
                                className={`text-sm font-semibold truncate max-w-[200px] ${isPlaying ? "text-amber-300" : "text-white"}`}
                              >
                                {track.title}
                              </p>
                              <p className="text-[11px] text-gray-600 truncate max-w-[200px] mt-0.5">
                                {track.artist}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Waveform */}
                        <td className="py-2.5 hidden lg:table-cell pr-4">
                          <WaveformBars isPlaying={isPlaying} count={20} />
                        </td>

                        {/* Genre */}
                        <td className="py-2.5 hidden md:table-cell">
                          {track.genre && (
                            <span
                              className="text-[10px] font-medium px-2 py-0.5 rounded-md"
                              style={{
                                background: "rgba(255,255,255,0.05)",
                                color: "#9ca3af",
                              }}
                            >
                              {track.genre}
                            </span>
                          )}
                        </td>

                        {/* Mood */}
                        <td className="py-2.5 hidden md:table-cell">
                          <span className="text-[11px] text-gray-600 capitalize">
                            {track.mood}
                          </span>
                        </td>

                        {/* Duration */}
                        <td className="py-2.5 pr-4 text-right">
                          <span className="text-[11px] text-gray-600 font-mono tabular-nums">
                            {formatDuration(track.duration)}
                          </span>
                        </td>

                        {/* Select */}
                        <td className="py-2.5 pr-4 w-10">
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                            style={{
                              background: isHovered
                                ? "rgba(217,119,6,0.2)"
                                : "transparent",
                              border: isHovered
                                ? "1px solid rgba(217,119,6,0.4)"
                                : "1px solid transparent",
                              opacity: isHovered ? 1 : 0,
                            }}
                          >
                            <Check size={12} className="text-amber-400" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicLibrary;
